import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type LanguageSwitcherLocale } from '@bbkAdminComponents/designer/language-switcher';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  useGetApplicationLocalesQuery,
  useGetAvailableLocalesQuery,
} from '@bbkAdminRedux/rtkq/languages.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';

type UseLanguageSwitcherLocalesOptions = {
  /** When true, only enabled locales are returned. Defaults to false. */
  enabledOnly?: boolean;
};

const useLanguageSwitcherLocalesQueries = () => {
  const appKey = useSelector(selectAppId);
  const isMultiLanguage = useFeatureFlags(FeatureFlagsE.MULTI_LANGUAGE);
  const appLocalesQuery = useGetApplicationLocalesQuery(
    { appKey },
    { skip: !isMultiLanguage }
  );
  const availableLocalesQuery = useGetAvailableLocalesQuery(undefined, {
    skip: !isMultiLanguage,
  });
  return { isMultiLanguage, appLocalesQuery, availableLocalesQuery };
};

/** Maps application locales to `LanguageSwitcherLocale[]` (primary first). */
export const useLanguageSwitcherLocales = ({
  enabledOnly = false,
}: UseLanguageSwitcherLocalesOptions = {}): LanguageSwitcherLocale[] => {
  const { appLocalesQuery, availableLocalesQuery } =
    useLanguageSwitcherLocalesQueries();
  const appLocales = appLocalesQuery.data ?? [];
  const availableLocales = availableLocalesQuery.data ?? [];

  return useMemo(() => {
    const nameFor = (code: string) =>
      availableLocales.find((a) => a.locale_code === code)?.display_name ??
      code;
    return [...appLocales]
      .filter((l) => (enabledOnly ? l.enabled : true))
      .sort((a, b) => (a.base === b.base ? 0 : a.base ? -1 : 1))
      .map((l) => ({
        code: l.locale_code,
        name: nameFor(l.locale_code),
        isPrimary: l.base,
        enabled: l.enabled,
      }));
  }, [appLocales, availableLocales, enabledOnly]);
};

/** Loading/fetching flags for the same locale queries as `useLanguageSwitcherLocales`. */
export const useLanguageSwitcherLocalesStatus = (): {
  isLoading: boolean;
  isFetching: boolean;
} => {
  const { isMultiLanguage, appLocalesQuery, availableLocalesQuery } =
    useLanguageSwitcherLocalesQueries();
  return {
    isLoading:
      isMultiLanguage &&
      (appLocalesQuery.isLoading || availableLocalesQuery.isLoading),
    isFetching:
      isMultiLanguage &&
      (appLocalesQuery.isFetching || availableLocalesQuery.isFetching),
  };
};
